import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { CalendarRange } from "lucide-react";
import { toast } from "sonner";
import { tanggalID } from "@/lib/format";

const pad = (n) => String(n).padStart(2, "0");
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/** Hitung rentang untuk preset (zona waktu lokal). Minggu dimulai hari Senin. */
export function presetRange(key) {
  const now = new Date();
  if (key === "today") return { start: ymd(now), end: ymd(now) };
  if (key === "week") {
    const s = new Date(now); const dow = (s.getDay() + 6) % 7; // Senin = 0
    s.setDate(s.getDate() - dow);
    return { start: ymd(s), end: ymd(now) };
  }
  if (key === "month") return { start: ymd(new Date(now.getFullYear(), now.getMonth(), 1)), end: ymd(now) };
  return { start: "", end: "" };
}

export function rangeLabel(v, defaultLabel) {
  if (!v || v.key === "default") return defaultLabel;
  if (v.key === "today") return `Hari Ini (${tanggalID(v.start)})`;
  if (v.key === "week") return `Minggu Ini (${tanggalID(v.start)} – ${tanggalID(v.end)})`;
  if (v.key === "month") return `Bulan Ini (${tanggalID(v.start)} – ${tanggalID(v.end)})`;
  return v.start === v.end ? tanggalID(v.start) : `${tanggalID(v.start)} – ${tanggalID(v.end)}`;
}

/**
 * Filter rentang grafik dashboard: default (N bulan terakhir), Hari Ini, Minggu Ini, Bulan Ini, Custom.
 * value = { key, start, end }; onChange dipanggil dengan value baru.
 */
export default function ChartRangeFilter({ value, onChange, defaultLabel = "6 Bulan Terakhir", testid = "chart-filter" }) {
  const [custom, setCustom] = useState({ start: value?.key === "custom" ? value.start : "", end: value?.key === "custom" ? value.end : "" });
  const [showCustom, setShowCustom] = useState(value?.key === "custom");
  const active = value?.key || "default";

  const pick = (key) => {
    if (key === "custom") { setShowCustom(true); return; }
    setShowCustom(false);
    onChange({ key, ...presetRange(key) });
  };
  const applyCustom = () => {
    if (!custom.start || !custom.end) { toast.error("Isi tanggal awal dan tanggal akhir"); return; }
    if (custom.start > custom.end) { toast.error("Tanggal awal tidak boleh setelah tanggal akhir"); return; }
    onChange({ key: "custom", start: custom.start, end: custom.end });
  };

  const opts = [
    { key: "default", label: defaultLabel }, { key: "today", label: "Hari Ini" },
    { key: "week", label: "Minggu Ini" }, { key: "month", label: "Bulan Ini" }, { key: "custom", label: "Custom" },
  ];
  return (
    <div className="flex flex-col items-start md:items-end gap-2" data-testid={testid}>
      <div className="inline-flex flex-wrap rounded-sm border border-slate-200 bg-white overflow-hidden">
        {opts.map((o) => {
          const on = o.key === "custom" ? (showCustom || active === "custom") : (active === o.key && !showCustom);
          return (
            <button key={o.key} type="button" onClick={() => pick(o.key)} data-testid={`${testid}-${o.key}`}
              className={`px-2.5 md:px-3 py-1.5 text-[11px] md:text-xs border-r last:border-r-0 border-slate-200 transition-colors ${on ? "brand-btn" : "text-slate-600 hover:bg-slate-50"}`}>
              {o.key === "custom" && <CalendarRange size={12} className="inline mr-1 -mt-0.5" />}{o.label}
            </button>
          );
        })}
      </div>
      {showCustom && (
        <div className="flex flex-wrap items-center gap-2" data-enter-scope>
          <Input type="date" value={custom.start} onChange={(e) => setCustom({ ...custom, start: e.target.value })} className="h-8 w-[150px] text-xs" data-testid={`${testid}-start`} />
          <span className="text-xs text-slate-400">s.d.</span>
          <Input type="date" value={custom.end} onChange={(e) => setCustom({ ...custom, end: e.target.value })} className="h-8 w-[150px] text-xs" data-testid={`${testid}-end`} />
          <Button size="sm" onClick={applyCustom} className="brand-btn rounded-sm h-8 text-xs" data-enter-submit data-testid={`${testid}-apply`}>Terapkan</Button>
        </div>
      )}
    </div>
  );
}
